import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { EditCollectionModal } from "./ui/EditCollectionModal";
import { CreateCollectionModal } from "./ui/CreateCollectionModal";
import { CollectionDeleteModal } from "./ui/CollectionDeleteModal";
import collectionsService from "../services/collectionsService";
import fileRelationService from "../services/fileRelationService";
import fileService from "../services/fileService";
import vectorService from "../services/vectorService";
import { useCollection } from "../contexts/CollectionContext";
import { useDashboardUI } from "../context/GlobalContext";
import { displayUserName } from "../helpers/helpers";

const STATUS_CONFIG = {
  true: {
    label: "Published",
    className: "bg-[#E7F7EE] text-[#12945A]",
  },
  false: {
    label: "Unpublished",
    className: "bg-[#FDECEC] text-[#D93636]",
  },
};

export function CollectionListSection() {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editIdx, setEditIdx] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [search, setSearch] = useState(""); // live value bound to the input
  const [debouncedSearch, setDebouncedSearch] = useState(""); // value actually sent to the API
  const [collections, setCollections] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // bump to force a refetch (create/delete)

  // Edit collection states
  const [isEditLoading, setIsEditLoading] = useState(false);

  // Delete collection states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [deletionStep, setDeletionStep] = useState(1);
  const [deletionStepMessage, setDeletionStepMessage] = useState("");

  const navigate = useNavigate();
  const { setCollection, setCollections: setContextCollections } =
    useCollection();
  const { facilityUserList, selectedFacilityFilter } = useDashboardUI();

  // Fetch collections from API
  const fetchCollections = async (
    pageNum = 1,
    limit = rowsPerPage,
    searchQuery = debouncedSearch
  ) => {
    try {
      setIsLoading(true);
      const response = await collectionsService.getCollections(
        pageNum,
        limit,
        searchQuery || null,
        selectedFacilityFilter?.facility_number ?? null
      );
      setCollections(response.collections);
      setPagination(response.pagination);

      // Also store all collections in context for persistence and sharing
      // Note: For now we'll store the current page. In production, you might want to
      // fetch all collections separately or accumulate them over time
      setContextCollections(response.collections);
    } catch (error) {
      console.error("Error fetching collections:", error);
      toast.error("Failed to load collections");
      setCollections([]);
      setPagination(null);
    } finally {
      setIsLoading(false);
      setIsInitialLoad(false);
    }
  };

  // Debounce the search box (repo convention: 400ms live search)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Single source of truth for fetching: runs on mount and whenever the page,
  // page size, debounced search, selected facility, or an explicit refresh change.
  useEffect(() => {
    fetchCollections(page, rowsPerPage, debouncedSearch);
  }, [page, rowsPerPage, debouncedSearch, selectedFacilityFilter, refreshTrigger]);

  // Pagination logic
  const total = pagination?.total_count || 0;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const currentCollections = collections || [];

  const handleRowsPerPage = (value) => {
    setRowsPerPage(Number(value));
    setPage(1);
    // The fetch effect refetches on rowsPerPage/page change.
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      // The fetch effect refetches on page change.
    }
  };

  // Handler for creating a new collection
  const handleCreateCollection = (newCollection) => {
    // Jump to page 1 and force a refetch (bump handles the already-on-page-1 case)
    setPage(1);
    setRefreshTrigger((t) => t + 1);
  };

  // Handler for editing a collection
  const handleEditCollection = async (updateData) => {
    if (editIdx === null) return;

    const collection = collections[editIdx];
    setIsEditLoading(true);

    try {
      const updatedCollection = await collectionsService.updateCollection(
        collection.id,
        updateData
      );

      // Update the specific collection in the local state
      setCollections((prevCollections) =>
        prevCollections.map((c, index) =>
          index === editIdx ? updatedCollection : c
        )
      );

      toast.success("Collection updated successfully");
      setEditIdx(null);
    } catch (error) {
      console.error("Error updating collection:", error);
      toast.error("Failed to update collection");
    } finally {
      setIsEditLoading(false);
    }
  };

  // Handler for opening delete confirmation
  const handleDeleteClick = async (collection) => {
    try {
      // Fetch collection with files to show in confirmation
      const collectionWithFiles = await collectionsService.getCollectionFiles(
        collection.id
      );
      setCollectionToDelete({
        ...collection,
        files: collectionWithFiles.files || [],
      });
      setDeleteModalOpen(true);
    } catch (error) {
      console.error("Error fetching collection files:", error);
      toast.error("Failed to load collection details");
    }
  };

  // Handler for confirming collection deletion
  const handleConfirmDelete = async () => {
    if (!collectionToDelete) return;

    setIsDeleteLoading(true);
    setDeletionStep(1);

    try {
      const files = collectionToDelete.files || [];

      // Step 1 of 4: Delete all file relations
      setDeletionStepMessage("Step 1 of 4: Deleting file relations...");
      for (const file of files) {
        await fileRelationService.deleteFileRelationByIds(
          file.id,
          collectionToDelete.id
        );
      }

      // Step 2 of 4: Delete from vector database
      setDeletionStep(2);
      setDeletionStepMessage("Step 2 of 4: Removing from vector database...");
      if (files.length > 0) {
        await vectorService.softDeleteDocuments(files);
      }

      // Step 3 of 4: Delete all files
      setDeletionStep(3);
      setDeletionStepMessage("Step 3 of 4: Deleting files...");
      for (const file of files) {
        await fileService.deleteFile(file.id);
      }

      // Step 4 of 4: Delete the collection
      setDeletionStep(4);
      setDeletionStepMessage("Step 4 of 4: Deleting collection...");
      await collectionsService.deleteCollection(collectionToDelete.id);

      // Remove collection from local state
      setCollections((prevCollections) =>
        prevCollections.filter((c) => c.id !== collectionToDelete.id)
      );

      // Update pagination if necessary
      if (pagination) {
        setPagination((prev) => ({
          ...prev,
          total_count: prev.total_count - 1,
        }));
      }

      toast.success("Collection deleted successfully");
      setDeleteModalOpen(false);
      setCollectionToDelete(null);
    } catch (error) {
      console.error("Error deleting collection:", error);
      toast.error(
        `Failed to delete collection: ${error.message || "Unknown error"}`
      );
    } finally {
      setIsDeleteLoading(false);
      setDeletionStep(1);
      setDeletionStepMessage("");
    }
  };

  const openCollection = (collection) => {
    setCollection(collection);
    navigate(`/collections/${collection.id}`);
  };

  // RENDER
  return (
    <>
      <CreateCollectionModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreate={handleCreateCollection}
      />
      <EditCollectionModal
        open={editIdx !== null}
        onClose={() => setEditIdx(null)}
        collection={editIdx !== null ? collections[editIdx] : null}
        onSave={handleEditCollection}
        isLoading={isEditLoading}
      />
      <CollectionDeleteModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setCollectionToDelete(null);
          setDeletionStep(1);
          setDeletionStepMessage("");
        }}
        onDelete={handleConfirmDelete}
        collection={collectionToDelete}
        files={collectionToDelete?.files || []}
        isLoading={isDeleteLoading}
        deletionStepMessage={deletionStepMessage}
      />

      <div className="w-full flex flex-col h-full lg:h-[calc(100vh-100px)] pt-4 gap-3">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 flex-shrink-0">
          <input
            type="text"
            placeholder="Search collections..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="min-w-0 flex-1 sm:flex-none sm:w-80 h-10 sm:h-11 rounded-xl border border-[#ECEEF4] bg-white px-4 sm:px-5 text-sm text-gray-700 placeholder:text-[#9AA1B2] transition-colors hover:border-[#D9DCE6] focus:outline-none focus:border-[#8B5CF6]/50 focus:ring-2 focus:ring-[#8B5CF6]/10"
          />

          <button
            onClick={() => setCreateModalOpen(true)}
            className="flex shrink-0 items-center gap-2 rounded-xl bg-[#3F4BB8] px-4 sm:px-6 h-11 text-sm font-semibold whitespace-nowrap text-white hover:bg-[#363FA0] transition"
          >
            <span className="hidden sm:inline">Create </span>New <span className="text-lg">+</span>
          </button>
        </div>

        {/* Table card (same look as Feedback Items / User Management / Support Tickets) */}
        <div className="relative flex-1 min-h-0 bg-white rounded-2xl overflow-hidden shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)]">
          {isLoading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-30">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mr-3"></div>
                <span className="text-gray-600">Loading collections...</span>
              </div>
            </div>
          )}

          <div className="table-scroll h-full overflow-auto">
          <table className="w-full min-w-[1000px] xl:min-w-[900px] table-fixed border-collapse text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                {[
                  { label: "Name", width: "20%" },
                  { label: "Description", width: "32%" },
                  { label: "Status", width: "13%" },
                  { label: "By", width: "13%" },
                  { label: "Date", width: "14%" },
                  { label: "Action", width: "8%" },
                ].map(({ label, width }) => (
                  <th
                    key={label}
                    style={{ position: "sticky", top: 0, background: "#F5F2FF", zIndex: 20, width, textAlign: "left" }}
                    className="py-3.5 pl-6 pr-3 align-middle text-xs font-semibold text-gray-500 uppercase tracking-wide"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentCollections.length > 0 ? (
                currentCollections.map((collection, index) => (
                  <tr
                    key={collection.id}
                    onClick={() => openCollection(collection)}
                    className="cursor-pointer border-b border-gray-100 last:border-0 transition-colors hover:bg-gray-50"
                  >
                    <td className="w-[20%] pl-6 pr-3 py-4 align-middle text-sm font-semibold text-[#14122B]">
                      {collection.name}
                    </td>

                    <td className="w-[32%] pl-6 pr-3 py-4 align-middle text-gray-600">
                      {collection.description}
                    </td>

                    <td className="w-[13%] pl-6 pr-3 py-4 align-middle">
                      <span
                        className={`inline-flex items-center whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${
                          STATUS_CONFIG[!!collection.is_published]?.className
                        }`}
                      >
                        {STATUS_CONFIG[!!collection.is_published]?.label}
                      </span>
                    </td>

                    <td className="w-[13%] pl-6 pr-3 py-4 align-middle text-gray-600">
                      {displayUserName(facilityUserList, collection.user_id)}
                    </td>

                    <td className="w-[14%] pl-6 pr-3 py-4 align-middle text-gray-600">
                      {collectionsService.formatDate(collection.created_at)}
                    </td>

                    <td className="w-[8%] pl-6 pr-3 py-4 align-middle text-left">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className="rounded-full p-2 hover:bg-gray-100"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5 text-gray-500"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <circle cx="10" cy="4" r="1.5" />
                              <circle cx="10" cy="10" r="1.5" />
                              <circle cx="10" cy="16" r="1.5" />
                            </svg>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="z-50 min-w-[140px] rounded-lg border border-gray-200 bg-white p-1 shadow-xl">
                          <DropdownMenuItem
                            className="cursor-pointer rounded-md bg-white hover:bg-gray-100 focus:bg-gray-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              const collectionIndex = collections.findIndex(
                                (c) => c.id === collection.id
                              );
                              setEditIdx(collectionIndex);
                            }}
                            disabled={isEditLoading || isDeleteLoading}
                          >
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="cursor-pointer rounded-md bg-white hover:bg-red-50 focus:bg-red-50 text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(collection);
                            }}
                            disabled={isEditLoading || isDeleteLoading}
                          >
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              ) : (
                !isLoading && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-10 text-center text-sm text-gray-500"
                    >
                      {search ? "Not Found" : "No Collections Found"}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
          </div>
        </div>

        {/* Pagination (same layout as Feedback Items / User Management) */}
        <div className="mt-auto pt-1 pb-1 flex flex-nowrap items-center justify-between gap-2 whitespace-nowrap text-sm flex-shrink-0">
          <span>Total {total}</span>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              Rows per page
              <select
                value={rowsPerPage}
                onChange={(e) => handleRowsPerPage(e.target.value)}
                className="rounded-md border px-2 py-1"
              >
                {[10, 20, 30].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                disabled={page === 1}
                onClick={() => handlePageChange(page - 1)}
                className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl disabled:opacity-40"
              >
                ‹
              </button>

              <span>
                {page} / {totalPages}
              </span>

              <button
                disabled={page === totalPages}
                onClick={() => handlePageChange(page + 1)}
                className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl disabled:opacity-40"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
